import React, { useState, useEffect } from "react";
import { useOrganizations } from "../../hooks/useOrganizations";
import { api } from "../../lib/api";
import { Button, Field, Alert, Modal } from "../../components";
import { fmtDate } from "../../lib/dates";
import "./Templates.css";

const fileName = (p) => (p || "").split("/").pop();

// Starting a report FROM a template is the inverse of starting one from a scan: the
// template is known and the scan is not. So this asks the one question that is actually
// open, rather than sending the user through a wizard that asks both.
//
// It also closes a real bug. This action used to route to the v1 two-step wizard passing
// `templateId`, but that page never reads templateId from router state — so the template
// the user had just chosen was silently dropped and they picked one again in step two.
const PickScanModal = ({ template, onClose, onPick }) => {
  const { organizations, loading: orgsLoading } = useOrganizations();
  const [organizationId, setOrganizationId] = useState("");
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scanPath, setScanPath] = useState("");

  useEffect(() => {
    if (!organizationId) { setScans([]); setScanPath(""); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        // POST, not GET — the internal scan list endpoint reads but is declared POST.
        const data = await api.post("s2/api/scan_results", { organizationid: organizationId });
        if (!cancelled) { setScans(data?.result || []); setScanPath(""); }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load that organization's scans.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  return (
    <Modal
      title={`Start a report from ${template?.name || "this template"}`}
      onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!scanPath} onClick={() => onPick(scanPath)}>
          Open in builder
        </Button>
      </>}
    >
      <Alert>{error}</Alert>

      <Field label="Organization" hint="Which client this report is for.">
        <select className="v2-input" value={organizationId} disabled={orgsLoading}
                onChange={(e) => setOrganizationId(e.target.value)}>
          <option value="">Choose an organization…</option>
          {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </Field>

      <Field label="Scan" hint="The report is built from this scan's data.">
        <select className="v2-input" value={scanPath}
                disabled={!organizationId || loading || scans.length === 0}
                onChange={(e) => setScanPath(e.target.value)}>
          <option value="">
            {!organizationId ? "Choose an organization first"
              : loading ? "Loading scans…"
              : scans.length === 0 ? "This organization has no scans"
              : "Choose a scan…"}
          </option>
          {scans.map((s) => (
            <option key={s.scan_id} value={s.file_path}>
              {fileName(s.file_path)} — {fmtDate(s.created_at)}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
};

export default PickScanModal;
